import { Box, useMediaQuery } from "@mui/material";
import React from "react";
import PropTypes from "prop-types";
import { canAccess } from "../../permissions";
import { hasRole } from "../../auth";
import AssessmentDashboard from "./AssessmentDashboard";
import AssessmentCourses from "./AssessmentCourses";
import AssessmentCourseCategories from "./AssessmentCourseCategories";
import AssessmentSubjects from "./AssessmentSubjects";
import AssessmentChapters from "./AssessmentChapters";
import AssessmentSynopsis from "./AssessmentSynopsis";
import AssessmentTopics from "./AssessmentTopics";
import AssessmentSubTopics from "./AssessmentSubTopics";
import AssessmentQuestions from "./AssessmentQuestions";
import AssessmentTests from "./AssessmentTests";
import AssessmentTestSeries from "./AssessmentTestSeries";
import AssessmentCoupons from "./AssessmentCoupons";
import AssessmentVideos from "./AssessmentVideos";
import AssessmentTestTypes from "./AssessmentTestTypes";
import AssessmentReports from "./AssessmentReports";
import AssessmentPayments from "./AssessmentPayments";
import AssessmentBadges from "./AssessmentBadges";
import AssessmentLeaderboard from "./AssessmentLeaderboard";
import AssessmentStudents from "./AssessmentStudents";
import AssessmentHeader from "./shell/AssessmentHeader";
import { AssessmentRail, AssessmentMenuButton } from "./shell/AssessmentSidebar";
import { GROUPS } from "./shell/assessmentNav";
import { cardSx, listScreenSx, SURFACE } from "./shell/assessmentTheme";

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

// Create this key on the UI Permissions admin page and grant it to the roles that may manage courses.
const COURSES_TAB_PERMISSION = "ASSESSMENT_ADMIN_MANAGE_CATEGORIES";
const TAB_STORAGE_KEY = "assessmentTabLabel";
const RAIL_STORAGE_KEY = "assessmentRailCollapsed";

const ALL_TABS = [
  { label: "Dashboard", group: "overview", Component: AssessmentDashboard },
  { label: "Exams", group: "setup", Component: AssessmentCourses },
  {
    label: "Courses",
    group: "delivery",
    Component: AssessmentCourseCategories,
    permission: COURSES_TAB_PERMISSION,
  },
  { label: "Subjects", group: "setup", Component: AssessmentSubjects },
  { label: "Chapters", group: "setup", Component: AssessmentChapters },
  { label: "Synopsis", group: "content", Component: AssessmentSynopsis },
  { label: "Topics", group: "setup", Component: AssessmentTopics },
  { label: "Sub Topics", group: "setup", Component: AssessmentSubTopics },
  { label: "Questions", group: "content", Component: AssessmentQuestions },
  { label: "Tests", group: "content", Component: AssessmentTests },
  { label: "Test Series", group: "delivery", Component: AssessmentTestSeries },
  { label: "Coupons", group: "delivery", Component: AssessmentCoupons, adminOnly: true },
  { label: "Videos", group: "content", Component: AssessmentVideos },
  { label: "Test Types", group: "setup", Component: AssessmentTestTypes },
  { label: "Reports", group: "overview", Component: AssessmentReports },
  { label: "Payments", group: "overview", Component: AssessmentPayments },
  { label: "Students", group: "students", Component: AssessmentStudents },
  { label: "Badges", group: "delivery", Component: AssessmentBadges },
  { label: "Leaderboard", group: "students", Component: AssessmentLeaderboard },
];

const isAdminRole = () => hasRole("ADMIN") || hasRole("ALL_FUNCTIONS");

// adminOnly tabs follow what the backend enforces for their endpoints (no permission key exists for them).
const canSeeTab = (tab) =>
  tab.adminOnly ? isAdminRole() : !tab.permission || canAccess(tab.permission) || isAdminRole();

const readSaved = (key) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const save = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch {}
};

function Assessment() {
  // Tabs the current user may see; indexes below refer to this list, so the open tab is
  // persisted by label (indexes shift when a gated tab is hidden).
  const tabs = React.useMemo(() => ALL_TABS.filter(canSeeTab), []);
  const [value, setValue] = React.useState(() =>
    Math.max(
      0,
      tabs.findIndex((t) => t.label === readSaved(TAB_STORAGE_KEY)),
    ),
  );
  const [collapsed, setCollapsed] = React.useState(
    () => readSaved(RAIL_STORAGE_KEY) === "1",
  );
  const isDesktop = useMediaQuery("(min-width:900px)");

  const select = (index) => {
    setValue(index);
    save(TAB_STORAGE_KEY, tabs[index].label);
  };

  // Lets a screen (e.g. the dashboard quick actions) jump to another tab by label.
  const goToLabel = (label) => {
    const index = tabs.findIndex((t) => t.label === label);
    if (index >= 0) select(index);
  };

  const toggleRail = () => {
    setCollapsed((c) => {
      save(RAIL_STORAGE_KEY, c ? "0" : "1");
      return !c;
    });
  };

  const current = tabs[value];
  const groupLabel = GROUPS.find((g) => g.key === current?.group)?.label;

  return (
    <Box sx={{ p: { xs: 1, md: 1.5 }, backgroundColor: SURFACE.page, minHeight: "100%" }}>
      <AssessmentHeader title={current.label} groupLabel={groupLabel} />

      <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
        {isDesktop && (
          <AssessmentRail
            tabs={tabs}
            value={value}
            onSelect={select}
            collapsed={collapsed}
            onToggle={toggleRail}
          />
        )}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          {!isDesktop && (
            <Box sx={{ mb: 1.5 }}>
              <AssessmentMenuButton tabs={tabs} value={value} onSelect={select} />
            </Box>
          )}

          <Box
            sx={{
              ...cardSx,
              minHeight: 320,
              ...(current.label !== "Dashboard" ? listScreenSx : {}),
            }}
          >
            {tabs.map((tab, index) => (
              <CustomTabPanel key={tab.label} value={value} index={index}>
                <tab.Component onNavigate={goToLabel} />
              </CustomTabPanel>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default Assessment;
